/**
 * Per-appointment "add to calendar" dropdown. Two paths:
 *  - Apple / Outlook / iPhone: download .ics file (system handler imports it)
 *  - Google Calendar: open the prefilled render URL in a new tab
 *
 * No backend dependency — both formats are built client-side from the
 * AppointmentRow we already have in render scope.
 */

import { CalendarPlus, Apple, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { buildIcs, googleCalUrl, downloadIcs } from '@/lib/calendar';

interface Props {
  appointmentId: string;
  startMs: number;
  /** Localised meeting title (e.g. "Besichtigung — Mr. Schmidt"). */
  title: string;
  durationMin?: number;
  location?: string | null;
  description?: string | null;
  /** Compact mode = icon-only square button (used in tight desktop table cells). */
  compact?: boolean;
}

export function AddToCalendarButton({
  appointmentId,
  startMs,
  title,
  durationMin,
  location,
  description,
  compact,
}: Props) {
  const { t } = useTranslation();

  const payload = {
    id: appointmentId,
    startMs,
    durationMin,
    title,
    location,
    description,
  };

  const onIcs = () => {
    const ics = buildIcs(payload);
    downloadIcs(`4m-termin-${appointmentId.slice(0, 8)}.ics`, ics);
  };

  const onGoogle = () => {
    window.open(googleCalUrl(payload), '_blank', 'noopener,noreferrer');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={t('appointments.calendar.cta', 'Zum Kalender hinzufügen')}
          className={
            compact
              ? 'inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2'
              : 'inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-line bg-card px-2.5 text-xs font-medium text-ink-2 hover:bg-paper-2'
          }
        >
          <CalendarPlus className="h-3 w-3" />
          {!compact && (
            <span>{t('appointments.calendar.cta_short', 'Kalender')}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onIcs} className="cursor-pointer">
          <Apple className="me-2 h-3.5 w-3.5" />
          <div className="flex flex-col leading-tight">
            <span>{t('appointments.calendar.ics', 'Apple / Outlook')}</span>
            <span className="text-[10px] text-ink-3">
              {t('appointments.calendar.ics_hint', '.ics herunterladen')}
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onGoogle} className="cursor-pointer">
          <Globe className="me-2 h-3.5 w-3.5" />
          <div className="flex flex-col leading-tight">
            <span>{t('appointments.calendar.google', 'Google Kalender')}</span>
            <span className="text-[10px] text-ink-3">
              {t('appointments.calendar.google_hint', 'In neuem Tab öffnen')}
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
